import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  Divider,
  Link,
  Chip
} from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import StarIcon from '@mui/icons-material/Star';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CandidateProfileSidebar from './CandidateProfileSidebar';

// Helper function to parse individual elements from complex query strings
function parseQueryElements(queryString) {
  if (!queryString) return [];
  
  // Remove outer parentheses if present
  let cleanQuery = queryString.trim();
  if (cleanQuery.startsWith('(') && cleanQuery.endsWith(')')) {
    cleanQuery = cleanQuery.slice(1, -1);
  }
  
  // Split by OR and AND, then clean up each element
  const elements = cleanQuery
    .split(/\s+(?:OR|AND)\s+/)
    .map(element => element.trim())
    .filter(element => element.length > 0)
    .map(element => {
      // Remove quotes and extra parentheses
      return element.replace(/^['"`(]+|['"`)]+$/g, '').trim();
    })
    .filter(element => element.length > 0);
  
  return elements;
}

// Helper function to get readable name for an element
function getReadableName(element, queries) {
  // If it's a hex ID (24 characters, alphanumeric), try to look it up
  if (/^[a-f0-9]{24}$/i.test(element) && queries && queries[element]) {
    return queries[element].name || element;
  }
  
  // If it contains a colon, it might be a field:value query - extract the value part
  if (element.includes(':')) {
    const parts = element.split(':');
    if (parts.length > 1) {
      let value = parts[parts.length - 1].trim();
      // Clean up the value
      value = value.replace(/^['"`(]+|['"`)]+$/g, '');
      return value;
    }
  }
  
  // Return as-is if it's already readable
  return element;
}

// Helper function to get matching criteria for a profile
function getMatchingCriteria(profileId, macroMappingData) {
  if (!macroMappingData || !macroMappingData.queryResults || !macroMappingData.autoMacroMap) {
    return [];
  }

  // Find the query result for this profile
  const queryResult = macroMappingData.queryResults.find(result => result.profile_id === profileId);
  if (!queryResult || !queryResult.macro_ids) {
    return [];
  }

  // Process each macro_id to extract readable criteria
  const allCriteria = [];
  
  queryResult.macro_ids.forEach(macroId => {
    const macroValue = macroMappingData.autoMacroMap[macroId];
    if (!macroValue) return;

    // Parse the macro value to extract individual elements
    const elements = parseQueryElements(macroValue);
    
    elements.forEach(element => {
      const readableName = getReadableName(element, macroMappingData.queries);
      
      // Only add if it's meaningful (not just hex IDs or technical terms)
      if (readableName && 
          readableName.length > 2 && 
          !readableName.startsWith('profile_type:') &&
          !/^[a-f0-9]{24}$/i.test(readableName)) {
        allCriteria.push(readableName);
      }
    });
  });

  // Remove duplicates and return
  return [...new Set(allCriteria)];
}

/**
 * Component to display a single candidate in the chat interface
 * @param {Object} props - Component props
 * @param {Object} props.candidate - The candidate data
 * @param {string} props.profileId - The profile ID
 * @param {number} props.currentIndex - The current candidate index
 * @param {number} props.totalCandidates - The total number of candidates
 * @param {Object} props.macroMappingData - The macro mapping data for matching criteria
 * @returns {JSX.Element} The rendered component
 */
const CandidatePreview = ({ 
  candidate, 
  profileId, 
  currentIndex,
  totalCandidates,
  macroMappingData
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Extract data from the candidate object
  const name = candidate?.candidate?.full_name ? unescape(candidate.candidate.full_name) : 'Jacky Harrison';
  
  // Get current title
  const title = candidate?.workexp
    ?.find(exp => exp.duration?.to_present)
    ?.projects?.map(proj => proj.role_and_group?.title)
    .filter(Boolean)[0] || 'Software Engineer';
  
  // Get company name
  const company = candidate?.workexp
    ?.find(exp => exp.duration?.to_present)
    ?.company?.name || 'Google';
  
  // Get location
  const city = candidate?.candidate?.location?.city || 'Vancouver';
  const state = candidate?.candidate?.location?.state || '';
  const country = candidate?.candidate?.location?.country || '';
  
  // Format location as "City, State" or "City, Country"
  let location = '';
  if (city) {
    if (state) {
      location = `${city}, ${state}`;
    } else if (country) {
      location = `${city}, ${country}`;
    } else {
      location = city;
    }
  } else {
    location = 'Vancouver';
  }

  // Get skills
  const skills = candidate?.skills || ['React.JS', 'user testing', 'time-management'];
  
  // Get years of experience
  const yearsOfExperience = candidate?.workexp?.reduce((total, exp) => {
    if (exp.duration) {
      const startYear = exp.duration.start_year || 0;
      const endYear = exp.duration.to_present ? new Date().getFullYear() : (exp.duration.end_year || 0);
      return total + (endYear - startYear);
    }
    return total;
  }, 0) || 4;

  // Get previous companies
  const previousCompanies = candidate?.workexp
    ?.filter(exp => !exp.duration?.to_present)
    ?.map(exp => exp.company?.name)
    ?.filter(Boolean) || ['Asana'];
  
  // Get a unique list of companies
  const uniqueCompanies = [company, ...previousCompanies].filter(Boolean).filter((value, index, self) => 
    self.indexOf(value) === index
  );

  // Generate a summary of the candidate
  const summary = `${name} is a seasoned software engineer with a proven track record at ${company}. With expertise in ${skills.slice(0, 3).join(', ')}, ${name} excels at delivering high-quality software solutions.`;

  const handleNameClick = () => {
    setSidebarOpen(true);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  return (
    <>
      <Paper
        elevation={1}
        sx={{
          borderRadius: '8px',
          overflow: 'hidden',
          width: '100%',
          border: '1px solid #e0e0e0',
          backgroundColor: 'white'
        }}
      >
        <Box sx={{ padding: '16px 20px' }}>
          {/* Header */}
          <Typography variant="h6" sx={{ color: '#6750A4', fontWeight: 'bold', mb: 1 }}>
            AI Candidate Summary
          </Typography>
          
          {/* Summary paragraph with clickable name */}
          <Typography variant="body1" sx={{ color: '#000', mb: 2 }}>
            <Link
              component="button"
              variant="inherit"
              onClick={handleNameClick}
              sx={{
                color: '#6750A4',
                fontWeight: 'bold',
                textDecoration: 'underline',
                cursor: 'pointer',
                '&:hover': {
                  color: '#5a3d99',
                  textDecoration: 'underline',
                },
              }}
            >
              {name}
            </Link>
            {summary.substring(name.length)}
          </Typography>
          
          <Divider sx={{ mb: 2 }} />
          
          {/* Details with icons */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Title */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <WorkIcon sx={{ color: '#6750A4' }} />
              <Typography variant="body1" sx={{ color: '#000' }}>
                <strong>Title:</strong> {title} at {company} for {Math.max(3, Math.round(yearsOfExperience))} years
              </Typography>
            </Box>
            
            <Divider sx={{ opacity: 0.5 }} />
            
            {/* Skills */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <StarIcon sx={{ color: '#6750A4' }} />
              <Typography variant="body1" sx={{ color: '#000' }}>
                <strong>Skills:</strong> Skilled in {skills.slice(0, 3).join(', ')}
                {skills.length > 3 ? ', and building efficient, user-friendly software' : ''}
              </Typography>
            </Box>
            
            <Divider sx={{ opacity: 0.5 }} />
            
            {/* Location */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <LocationOnIcon sx={{ color: '#6750A4' }} />
              <Typography variant="body1" sx={{ color: '#000' }}>
                <strong>Location:</strong> Based in {location} for the past {Math.max(2, Math.round(yearsOfExperience/2))} years
              </Typography>
            </Box>
            
            <Divider sx={{ opacity: 0.5 }} />
            
            {/* Matching Criteria */}
            {(() => {
              // Debug logging
              console.log('CandidatePreview Debug:', {
                profileId,
                macroMappingData: macroMappingData ? 'exists' : 'null',
                hasQueryResults: macroMappingData?.queryResults ? macroMappingData.queryResults.length : 0,
                hasAutoMacroMap: macroMappingData?.autoMacroMap ? Object.keys(macroMappingData.autoMacroMap).length : 0
              });

              const matchingCriteria = getMatchingCriteria(profileId, macroMappingData);
              console.log('CandidatePreview matching criteria for profile:', profileId, matchingCriteria);
              
              // Only show real matching criteria - no fallback
              if (matchingCriteria.length > 0) {
                return (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <CheckCircleIcon sx={{ color: '#6750A4', mt: 0.5 }} />
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                        <Typography variant="body1" sx={{ color: '#000' }}>
                          <strong>Matching Criteria:</strong>
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {matchingCriteria.slice(0, 4).map((criterion, index) => (
                            <Chip
                              key={index}
                              label={criterion}
                              size="small"
                              icon={<CheckCircleIcon />}
                              color="success"
                              variant="outlined"
                              sx={{
                                fontSize: '0.7rem',
                                height: '22px',
                                maxWidth: '200px', // Limit chip width
                                '& .MuiChip-label': {
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                },
                                '& .MuiChip-icon': {
                                  fontSize: '0.8rem',
                                  width: '14px',
                                  height: '14px',
                                }
                              }}
                            />
                          ))}
                          {matchingCriteria.length > 4 && (
                            <Chip
                              label={`+${matchingCriteria.length - 4} more`}
                              size="small"
                              color="default"
                              variant="outlined"
                              sx={{
                                fontSize: '0.7rem',
                                height: '22px'
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                    
                    <Divider sx={{ opacity: 0.5 }} />
                  </>
                );
              }
              return null;
            })()}
            
            {/* Experience */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <AccessTimeIcon sx={{ color: '#6750A4' }} />
              <Typography variant="body1" sx={{ color: '#000' }}>
                <strong>Experience:</strong> {Math.max(4, Math.round(yearsOfExperience))}+ years of experience, has worked at {uniqueCompanies.slice(0, 2).join(' and ')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Candidate Profile Sidebar */}
      <CandidateProfileSidebar
        open={sidebarOpen}
        onClose={handleSidebarClose}
        candidate={candidate}
        profileId={profileId}
        macroMappingData={macroMappingData}
      />
    </>
  );
};

export default CandidatePreview;
